import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import Breadcrumbs from "../../../../components/BreadCrumbs";
import {
  ReportView,
  ResolveReportModal,
  buildEventScopedReportBreadcrumbs,
  mapLogisticsIssueComments,
  mapReportDetailToViewModel,
} from "../../../../components/Events/Event/Reports/ReportView";
import useReportViewLabels from "../../../../components/Events/Event/Reports/ReportView/useReportViewLabels";
import { REPORT_ISSUER_TYPES } from "../../../../components/Events/Event/Reports/reportMappers";
import messages from "../../../../assets/locale/messages";
import { adminPermissionModules } from "../../../../utils/Constants";
import { useAdminModulePermission } from "../../../../utils/adminPortalHelpers";
import { ROUTE_PATHS } from "../../../../utils/RoutesPaths";
import {
  createReportCommentRequest,
  getReportCommentsRequest,
  getReportDetailRequest,
  updateReportRequest,
} from "../../../../store/Reports/actions";

const ViewReport = () => {
  const { event_id, id } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.locale.lang);
  const { report, shared, events } = messages[lang];
  const detail = useSelector((state) => state.report.detail);
  const comments = useSelector((state) => state.report.comments);
  const updating = useSelector((state) => state.report.updating);
  const commenting = useSelector((state) => state.report.commenting);
  const labels = useReportViewLabels(report, shared);
  const [resolveOpen, setResolveOpen] = useState(false);
  const { canAdd: canCommentPermission, canEdit: canResolvePermission } =
    useAdminModulePermission(adminPermissionModules.manageReports);

  useEffect(() => {
    if (!event_id || !id) return;
    dispatch(getReportDetailRequest({ event_id, id }));
    dispatch(
      getReportCommentsRequest({
        id,
        params: { page: -1 },
      }),
    );
  }, [dispatch, event_id, id, lang]);

  const viewModel = useMemo(() => {
    const base = mapReportDetailToViewModel(detail, {
      lang,
      labels: labels.view,
    });
    if (!base) return null;

    const mappedComments = mapLogisticsIssueComments(comments);
    return {
      ...base,
      comments: mappedComments.length ? mappedComments : base.comments,
    };
  }, [comments, detail, lang, labels.view]);

  const breadcrumbs = useMemo(() => {
    if (!event_id) return [];

    const eventViewPath = ROUTE_PATHS.viewEvent.replace(":id", event_id);
    const eventName =
      detail?.event?.name ||
      detail?.event_name ||
      viewModel?.eventName ||
      "";
    const mappedTitle =
      viewModel?.title && viewModel.title !== "-" ? viewModel.title : null;
    const reportTitle =
      (detail?.title && String(detail.title).trim()) ||
      mappedTitle ||
      report.listReportBreadcrumb;

    return buildEventScopedReportBreadcrumbs({
      eventsListPath: ROUTE_PATHS.events,
      eventViewPath,
      eventStatus: detail?.event?.status || detail?.event_status,
      eventName,
      reportIdLabel: reportTitle,
      reportCrumbLabel: report.viewReport.breadcrumbReport,
      eventsMessages: events,
    });
  }, [
    detail,
    event_id,
    events,
    report.listReportBreadcrumb,
    report.viewReport.breadcrumbReport,
    viewModel,
  ]);

  const handleResolveConfirm = ({ comment, imageUrl, imageUrls } = {}) => {
    const urls = Array.isArray(imageUrls)
      ? imageUrls.filter(Boolean)
      : imageUrl
        ? [imageUrl]
        : [];
    dispatch(
      updateReportRequest({
        event_id,
        id,
        data: {
          status: "closed",
          ...(comment
            ? { comment, resolution_description: comment }
            : {}),
          ...(urls.length
            ? {
                logistics_issue_images_attributes: urls.map((image_url) => ({
                  image_url,
                })),
              }
            : {}),
        },
        onSuccess: () => {
          setResolveOpen(false);
          dispatch(getReportDetailRequest({ event_id, id }));
        },
      }),
    );
  };

  const handlePostComment = (description) => {
    if (!id || !description) return;
    dispatch(
      createReportCommentRequest({
        id,
        data: { description },
        onSuccess: () => {
          dispatch(
            getReportCommentsRequest({
              id,
              params: { page: -1 },
            }),
          );
        },
      }),
    );
  };

  const handleViewProfile = () => {
    const reporterId = viewModel?.reporterId;
    if (!reporterId) return;

    const type = viewModel?.reporterType;
    if (type === REPORT_ISSUER_TYPES.supervisor) {
      history.push(ROUTE_PATHS.viewApplicant.replace(":id", reporterId));
      return;
    }
    if (type === REPORT_ISSUER_TYPES.employee) {
      const companyId =
        detail?.event?.company?.id ||
        detail?.event?.company_id ||
        detail?.company?.id ||
        detail?.company_id;
      if (!companyId) return;
      history.push({
        pathname: ROUTE_PATHS.companiesListCompanyDetails.replace(
          ":id",
          companyId,
        ),
        search: "?tab=overview",
      });
    }
  };

  const canViewReporterProfile = (() => {
    if (!viewModel?.reporterId) return false;
    const { reporterType: type } = viewModel;
    if (type === REPORT_ISSUER_TYPES.supervisor) return true;
    if (type === REPORT_ISSUER_TYPES.employee) {
      return Boolean(
        detail?.event?.company?.id ||
          detail?.event?.company_id ||
          detail?.company?.id ||
          detail?.company_id,
      );
    }
    return false;
  })();

  return (
    <div className="events-list-container">
      <div className="page-title-margin">
        <Breadcrumbs list={breadcrumbs} BreadCrumbHasBackIcon={false} />
      </div>

      {viewModel ? (
        <ReportView
          report={viewModel}
          labels={labels.view}
          canResolve={canResolvePermission && viewModel.isOpen}
          canComment={canCommentPermission && viewModel.isOpen}
          commenting={commenting}
          onResolve={() => setResolveOpen(true)}
          onPostComment={handlePostComment}
          onViewProfile={canViewReporterProfile ? handleViewProfile : undefined}
        />
      ) : null}

      <ResolveReportModal
        open={resolveOpen}
        labels={labels.resolve}
        submitting={updating}
        onClose={() => setResolveOpen(false)}
        onConfirm={handleResolveConfirm}
      />
    </div>
  );
};

export default ViewReport;
